import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getMenuConfig } from '../lib/api';
import type { MenuConfigDto } from '../lib/api-types';
import type { FooterColumn, MenuConfig, MenuItem } from '../data/menus';
import {
  DEFAULT_FOOTER_SETTINGS,
  DEFAULT_HEADER_SETTINGS,
  DEFAULT_MENUS,
  loadMenus,
} from '../data/menus';

interface MenuConfigContextValue {
  config: MenuConfig;
  ready: boolean;
}

const MenuConfigContext = createContext<MenuConfigContextValue | null>(null);

export function coerceMenuConfig(raw: MenuConfigDto): MenuConfig {
  const footerSettings = {
    ...DEFAULT_FOOTER_SETTINGS,
    ...(typeof raw.footerSettings === 'object' && raw.footerSettings
      ? (raw.footerSettings as Record<string, unknown>)
      : {}),
  };
  const headerSettings = {
    ...DEFAULT_HEADER_SETTINGS,
    ...(typeof raw.headerSettings === 'object' && raw.headerSettings
      ? (raw.headerSettings as Record<string, unknown>)
      : {}),
  };
  const header = Array.isArray(raw.header)
    ? (raw.header as MenuItem[])
    : DEFAULT_MENUS.header;
  let footerColumns = Array.isArray(raw.footerColumns)
    ? (raw.footerColumns as FooterColumn[])
    : DEFAULT_MENUS.footerColumns;

  if (!footerColumns.some((col) => col.contentType === 'brand')) {
    footerColumns = [
      {
        id: `f_col_brand_api_${Date.now()}`,
        title: { en: '', ar: '' },
        width: footerSettings.brandColumnWidth || 'wide',
        contentType: 'brand',
        items: [],
        brandLogoUrl: footerSettings.logoUrl,
        brandLogoAlt: footerSettings.logoAlt,
        brandLogoHeight: footerSettings.logoHeight,
        brandTagline: footerSettings.tagline,
      },
      ...footerColumns,
    ];
  }

  return {
    header,
    footerColumns,
    footerSettings,
    headerSettings,
  };
}

export function MenuConfigProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [config, setConfig] = useState<MenuConfig>(() => ({
    ...DEFAULT_MENUS,
    footerSettings: DEFAULT_FOOTER_SETTINGS,
    headerSettings: DEFAULT_HEADER_SETTINGS,
  }));
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await getMenuConfig();
        if (!cancelled) setConfig(coerceMenuConfig(raw));
      } catch {
        console.warn(
          '[api] menu config fetch failed, using bundled fallback',
        );
        if (!cancelled) setConfig(loadMenus());
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => ({ config, ready }), [config, ready]);

  return (
    <MenuConfigContext.Provider value={value}>
      {children}
    </MenuConfigContext.Provider>
  );
}

export function useMenuConfig(): MenuConfigContextValue {
  const ctx = useContext(MenuConfigContext);
  if (!ctx) {
    throw new Error('useMenuConfig must be used within MenuConfigProvider');
  }
  return ctx;
}
